import { IconFileText, IconFlagFilled, IconLayoutKanban, IconMessageCircle, IconPlus } from "../../icons";
import React, { useState } from "react";
import { DateField } from "../../DateField";
import { BlackLink } from "../../Link";
import { PieChart } from "../../PieChart";
import { Tooltip } from "../../Tooltip";
import * as Types from "../types";
import { PersonField } from "../../PersonField";
import { EmptyMilestoneDropZone } from "./EmptyMilestoneDropZone";
import { TaskCreationModal } from "../../TaskCreationModal";
import { TaskList } from "./TaskList";
import { sortTasks } from "../utils/sortTasks";
import { InlineTaskCreator } from "./InlineTaskCreator";
import { useInlineTaskCreator } from "../hooks/useInlineTaskCreator";
import { SecondaryButton } from "../../Button";
import { StatusSelector } from "../../StatusSelector";
import classNames from "../../utils/classnames";
import { createTestId } from "../../TestableElement";
import type { BoardLocation } from "../../utils/PragmaticDragAndDrop";
import type { RichEditorHandlers } from "../../RichEditor/useEditor";
import type { FormattedTimePreferences } from "../../FormattedTime";

export interface MilestoneCardProps {
  milestone: Types.Milestone;
  tasks: Types.Task[];
  showHiddenTasksToggle?: boolean;
  showKanbanLink?: boolean;
  onTaskCreate: (task: Types.NewTaskPayload) => void;
  onTaskAssigneeChange: (taskId: string, assignees: Types.Person[]) => void;
  onTaskDueDateChange: (taskId: string, dueDate: DateField.ContextualDate | null) => void;
  onTaskStatusChange: (taskId: string, status: Types.Status | null) => void;
  onMilestoneUpdate?: (milestoneId: string, updates: Types.UpdateMilestonePayload) => void;
  assigneePersonSearch?: PersonField.SearchData;
  statusOptions: StatusSelector.StatusOption[];
  availableMilestones?: Types.Milestone[];
  draggedItemId?: string | null;
  targetLocation?: BoardLocation | null;
  placeholderHeight?: number | null;
  selectedTaskId?: string | null;
  onTaskClick?: (taskId: string) => void;
  onInlineCreateOpen?: () => void;
  richTextHandlers?: RichEditorHandlers;
  formattedTimePreferences: FormattedTimePreferences;

  /**
   * Milestone statistics - if not provided, will be calculated from tasks
   */
  stats?: Types.MilestoneStats;
}

/**
 * MilestoneCard component displays a milestone with its tasks
 * It combines the milestone header with either a TaskList or EmptyMilestoneDropZone
 */
export function MilestoneCard({
  milestone,
  tasks,
  showHiddenTasksToggle = false,
  showKanbanLink = false,
  onTaskCreate,
  onTaskAssigneeChange,
  onTaskDueDateChange,
  onTaskStatusChange,
  onMilestoneUpdate,
  assigneePersonSearch,
  statusOptions,
  stats,
  availableMilestones = [],
  draggedItemId = null,
  targetLocation = null,
  placeholderHeight = null,
  selectedTaskId = null,
  onTaskClick,
  onInlineCreateOpen,
  richTextHandlers,
  formattedTimePreferences,
}: MilestoneCardProps) {
  const cardRef = React.useRef<HTMLLIElement>(null);
  const sortedTasks = React.useMemo(() => sortTasks(tasks, milestone), [tasks, milestone.tasksOrderingState]);
  const isCompleted = milestone.status === "done";
  const showOverdueWarning = !isCompleted;

  // Generate default stats if not provided
  const milestoneStats = stats || calculateMilestoneStats(sortedTasks);
  const completionPercentage = calculateCompletionPercentage(milestoneStats);
  const completionLabel = `${Math.round(completionPercentage)}% complete`;
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const {
    open: creatorOpen,
    openCreator,
    closeCreator,
    creatorRef,
    hoverBind,
  } = useInlineTaskCreator({
    onOpen: onInlineCreateOpen,
    isActive: () => {
      const activeElement = document.activeElement;
      return activeElement instanceof HTMLElement && Boolean(cardRef.current?.contains(activeElement));
    },
  });

  const handleCreateTask = (newTask: Types.NewTaskPayload) => {
    if (onTaskCreate) {
      onTaskCreate(newTask);
    }
    // Close modal if not creating multiple tasks
    // (the modal handles this internally)
  };

  const handleInlineTaskCreate = (title: string) => {
    handleCreateTask({ title, milestone, dueDate: null, assignees: [] });
  };

  const handleMilestoneDueDateChange = (newDueDate: DateField.ContextualDate | null) => {
    if (onMilestoneUpdate) {
      onMilestoneUpdate(milestone.id, { name: milestone.name, dueDate: newDueDate || null });
    }
  };

  // Do not auto-open on empty milestones; rely on explicit triggers

  return (
    <>
      <li ref={cardRef} {...hoverBind}>
        {/* Milestone header */}
        <div
          className={classNames(
            "flex items-center justify-between gap-3 px-4 py-3 bg-surface-dimmed border-b border-surface-outline",
            !milestone.dueDate && onMilestoneUpdate ? "group/milestone-header" : undefined,
          )}
          data-test-id={createTestId("milestone", milestone.id)}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {isCompleted ? (
              <div className="flex items-center gap-1.5 flex-shrink-0 text-accent-1">
                <IconFlagFilled size={16} className="flex-shrink-0" />
              </div>
            ) : (
              <PieChart
                size={16}
                ariaLabel={`Milestone progress: ${completionLabel}`}
                title={`Milestone progress: ${completionLabel}`}
                slices={[
                  {
                    percentage: completionPercentage,
                    color: "var(--color-callout-success-content)",
                  },
                ]}
              />
            )}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <BlackLink
                to={milestone.link || ""}
                // Hover color change on medium+ screens (hover disabled on small screens to prevent double-tap on mobile)
                className="min-w-0 text-sm font-semibold text-content-base transition-colors md:hover:text-link-hover"
                underline="hover"
                title={milestone.name}
              >
                <span className="overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] sm:block sm:truncate">
                  {milestone.name}
                </span>
              </BlackLink>

              {/* Milestone indicators */}
              <div className="flex items-center gap-1 flex-shrink-0 text-content-dimmed">
                {milestone.hasDescription && (
                  <span className="flex items-center" data-test-id="description-indicator">
                    <IconFileText size={12} />
                  </span>
                )}

                {milestone.hasComments && (
                  <span className="flex items-center" data-test-id="comments-indicator">
                    <IconMessageCircle size={12} />
                    {milestone.commentCount && <span className="ml-0.5 text-xs">{milestone.commentCount}</span>}
                  </span>
                )}
              </div>

              {/* Due date indicator */}
              <div className="group/milestone-due-date flex-shrink-0 flex items-center pl-1">
                {milestone.dueDate || !onMilestoneUpdate ? (
                  <DateField
                    date={milestone.dueDate || null}
                    onDateSelect={handleMilestoneDueDateChange}
                    variant="inline"
                    showOverdueWarning={showOverdueWarning}
                    placeholder="Set due date"
                    readonly={!onMilestoneUpdate}
                    size="small"
                  />
                ) : (
                  // Show date picker on hover on medium+ screens (hover disabled on small screens to prevent double-tap on mobile)
                  <div className="opacity-0 transition-opacity md:group-hover/milestone-due-date:opacity-100 md:group-hover/milestone-header:opacity-100">
                    <DateField
                      date={null}
                      onDateSelect={handleMilestoneDueDateChange}
                      variant="inline"
                      showOverdueWarning={showOverdueWarning}
                      placeholder="Set due date"
                      readonly={false}
                      size="small"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {showKanbanLink && milestone.kanbanLink && (
              <Tooltip content="Open Kanban" size="sm">
                <BlackLink
                  to={milestone.kanbanLink}
                  className="flex items-center text-content-dimmed md:hover:text-content-base transition-colors"
                  underline="hover"
                >
                  <IconLayoutKanban size={18} className="text-content-dimmed" />
                  <span className="sr-only">Open Kanban</span>
                </BlackLink>
              </Tooltip>
            )}

            <SecondaryButton
              size="xs"
              icon={IconPlus}
              className="min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"
              onClick={openCreator}
              testId="milestone-add-task"
            >
              {/* icon-only for reduced repetition; keep accessible label */}
              <span className="sr-only">Add task</span>
            </SecondaryButton>
          </div>
        </div>

        {/* Tasks in this milestone - show empty state when no tasks at all */}
        {sortedTasks && sortedTasks.length > 0 ? (
          <TaskList
            tasks={sortedTasks}
            showHiddenTasksToggle={showHiddenTasksToggle}
            milestoneId={milestone.id}
            onTaskAssigneeChange={onTaskAssigneeChange}
            onTaskDueDateChange={onTaskDueDateChange}
            onTaskStatusChange={onTaskStatusChange}
            assigneePersonSearch={assigneePersonSearch}
            statusOptions={statusOptions}
            draggedItemId={draggedItemId}
            targetLocation={targetLocation}
            placeholderHeight={placeholderHeight}
            selectedTaskId={selectedTaskId}
            onTaskClick={onTaskClick}
            suppressEmptyStateWhenOnlyHiddenTasks={isCompleted}
            inlineCreateRow={
              creatorOpen ? (
                <InlineTaskCreator
                  ref={creatorRef}
                  onCreate={handleInlineTaskCreate}
                  onRequestAdvanced={() => setIsTaskModalOpen(true)}
                  onCancel={closeCreator}
                  autoFocus
                  testId="inline-task-creator"
                />
              ) : undefined
            }
          />
        ) : (
          <EmptyMilestoneDropZone
            milestoneId={milestone.id}
            draggedItemId={draggedItemId}
            targetLocation={targetLocation}
            placeholderHeight={placeholderHeight}
          >
            {creatorOpen ? (
              <>
                <InlineTaskCreator
                  ref={creatorRef}
                  onCreate={handleInlineTaskCreate}
                  onRequestAdvanced={() => setIsTaskModalOpen(true)}
                  onCancel={closeCreator}
                  autoFocus
                  testId="inline-task-creator-empty"
                />
                <div className="hidden px-4 pb-3 text-center text-content-subtle text-xs sm:block">
                  Press Enter to add. You can also drag tasks here.
                </div>
              </>
            ) : (
              <div className="text-left text-content-subtle text-sm sm:text-center">
                <span className="sm:hidden">Tap + to add a task.</span>
                <span className="hidden sm:inline">Click + or press 'c' to add a task, or drag a task here.</span>
              </div>
            )}
          </EmptyMilestoneDropZone>
        )}
      </li>

      {richTextHandlers && (
        <TaskCreationModal
          variant="project"
          assigneePersonSearch={assigneePersonSearch}
          isOpen={isTaskModalOpen}
          onClose={() => setIsTaskModalOpen(false)}
          onCreateTask={handleCreateTask}
          milestones={availableMilestones.length > 0 ? availableMilestones : [milestone]}
          onMilestoneSearch={async () => {}} // No-op: milestones list is static in this context
          currentMilestoneId={milestone.id}
          richTextHandlers={richTextHandlers}
          formattedTimePreferences={formattedTimePreferences}
        />
      )}
    </>
  );
}

/**
 * Calculate statistics for a milestone based on its tasks
 */
export const calculateMilestoneStats = (tasks: Types.Task[]) => {
  const stats = {
    pending: 0,
    inProgress: 0,
    done: 0,
    canceled: 0,
    total: 0,
  };

  // Skip empty task lists
  if (!tasks || tasks.length === 0) {
    return stats;
  }

  // Count tasks by status
  tasks.forEach((task) => {
    // Don't count helper tasks
    if (task._isHelperTask) return;

    stats.total++;

    switch (task.status?.color) {
      case "gray":
        stats.pending++;
        break;
      case "blue":
        stats.inProgress++;
        break;
      case "green":
        stats.done++;
        break;
      case "red":
        stats.canceled++;
        break;
    }
  });

  return stats;
};

/**
 * Calculate completion percentage for a milestone, excluding canceled tasks
 * If a milestone has 1 done task and 1 canceled task, it should show as 100% complete
 */
function calculateCompletionPercentage(stats: {
  pending: number;
  inProgress: number;
  done: number;
  canceled: number;
  total: number;
}) {
  // Active tasks are those that aren't canceled
  const activeTasks = stats.total - stats.canceled;

  // If there are no active tasks, show as 0% complete
  if (activeTasks === 0) return 0;

  // Calculate percentage based only on active tasks
  return (stats.done / activeTasks) * 100;
}

export default MilestoneCard;
